// src/components/Header.jsx (actualizado con user info y logout)
import React, { useState, useEffect } from 'react';
import logoImg from '../assets/logo_tracking.png';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeNav, setActiveNav] = useState('Inicio');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const detectCurrentPage = () => {
      const path = window.location.pathname;
      if (path === '/servicio-al-cliente' || path.includes('servicio')) {
        setActiveNav('ServicioAlCliente');
      } else {
        setActiveNav('Inicio');
      }
    };

    // Cargar información del usuario desde localStorage
    const loadUserData = () => {
      const authData = localStorage.getItem('packsys_auth');
      if (authData) {
        try {
          const userData = JSON.parse(authData);
          setUser(userData);
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    detectCurrentPage();
    loadUserData();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('packsys_auth');
    setUser(null);
    window.location.href = '/';
  };

  const handleNavClick = (itemName, itemHref) => {
    setActiveNav(itemName);
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    {
      name: 'Inicio',
      href: '/tracking',
      displayName: 'Inicio',
    },
    {
      name: 'ServicioAlCliente',
      href: '/servicio-al-cliente',
      displayName: 'Servicio Al Cliente',
    },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white py-3 border-b border-gray-100' : 'bg-white py-6'
      }`}
    >
      <div className="max-w-12xl mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <a href="/">
              <img
                className={`transition-all duration-500 ${
                  isScrolled ? 'h-10 w-40' : 'h-12 w-48'
                } hover:scale-105 transform`}
                src={logoImg}
                alt="Logo Packsys"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 bg-white/80 rounded-2xl px-2 py-2 shadow-lg border border-gray-100">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={() => handleNavClick(item.name, item.href)}
                className={`relative px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeNav === item.name
                    ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg'
                    : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                {item.displayName}
                {activeNav === item.name && (
                  <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-blue-600 rounded-full"></div>
                )}
              </a>
            ))}
          </nav>

          {/* User Info & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-3">
                {/* User Info */}
                <div className="flex items-center space-x-2 bg-blue-50 rounded-xl px-4 py-2 border border-blue-100">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700">
                      {user.name.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">
                      Usuario autorizado
                    </span>
                  </div>
                </div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 transform flex items-center space-x-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  <span>Cerrar Sesión</span>
                </button>
              </div>
            ) : (
              <a
                href="https://packsys.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-packsys-orange to-orange-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 transform hover:from-orange-500 hover:to-orange-600"
              >
                Tienda Packsys
              </a>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden transition-all duration-300 p-2 rounded-lg ${
              isMobileMenuOpen
                ? 'bg-blue-100 text-blue-600'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-500 overflow-hidden ${
            isMobileMenuOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-gray-100 p-6">
            <nav className="flex flex-col space-y-3">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={() => handleNavClick(item.name, item.href)}
                  className={`px-4 py-3 rounded-xl font-semibold transition-all duration-300 text-center ${
                    activeNav === item.name
                      ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  {item.displayName}
                </a>
              ))}

              {/* User Info Mobile */}
              {user && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700 truncate">
                        {user.name.toUpperCase()}
                      </p>
                      <p className="text-xs text-gray-500">
                        Usuario autorizado
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              )}

              {!user && (
                <a
                  href="https://packsys.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 bg-gradient-to-r from-packsys-orange to-orange-500 text-white px-4 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                >
                  Tienda Packsys
                </a>
              )}
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
